import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'

export const useBiosecurityResultsStore = defineStore('biosecurityResults', () => {
  // Map of botanical name (lowercase) to biosecurity information
  const biosecurityResults = ref(new Map<string, {
    originalName: string
    species: string
    totalPests: number
    majorHostCount: number
    hostCount: number 
    weedWildCount: number
    riskLevel: 'major_host' | 'host' | 'weed_wild' | 'none' // For RAG colormap
    riskScore: number // For sequential/divergent colormaps (0-1 normalized)
  }>())

  // Global biosecurity metrics for the current dataset
  const globalBiosecurityMetrics = ref({
    totalSpecies: 0,
    averagePestsPerSpecies: 0,
    maxPests: 0,
    minPests: 0,
    speciesWithMajorHosts: 0,
    speciesWithHosts: 0,
    speciesWithWeedWild: 0,
    speciesWithNoPests: 0,
    isValid: false
  })

  // Reactive getters
  const getBiosecurityResults = computed(() => biosecurityResults.value)
  const getGlobalMetrics = computed(() => globalBiosecurityMetrics.value)
  
  const getBiosecurityDataForName = (botanicalName: string) => {
    return biosecurityResults.value.get(botanicalName.toLowerCase()) || null
  }

  const hasBiosecurityResult = (botanicalName: string): boolean => {
    return biosecurityResults.value.has(botanicalName.toLowerCase())
  }

  // Get risk level for RAG colormap
  const getRiskLevel = (botanicalName: string): 'major_host' | 'host' | 'weed_wild' | 'none' | null => {
    const result = biosecurityResults.value.get(botanicalName.toLowerCase())
    return result?.riskLevel || null
  }

  // Get normalized risk score for sequential/divergent colormaps (0-1 scale)
  const getRiskScore = (botanicalName: string): number | null => {
    const result = biosecurityResults.value.get(botanicalName.toLowerCase())
    return result?.riskScore || null
  }

  // Get linearly scaled pest count that maps actual data range to full 0-1 colormap
  const getScaledPestCount = (botanicalName: string): number | null => {
    const result = biosecurityResults.value.get(botanicalName.toLowerCase())
    if (!result) return null

    // Get all pest counts to find min/max of actual data
    const allPestCounts = Array.from(biosecurityResults.value.values())
      .map(r => r.totalPests)

    if (allPestCounts.length === 0) return null

    const minPests = Math.min(...allPestCounts)
    const maxPests = Math.max(...allPestCounts)

    // If all species have equal pest count, return middle value
    if (minPests === maxPests) return 0.5

    // Linear scaling: map [minPests, maxPests] to [0, 1]
    const scaledValue = (result.totalPests - minPests) / (maxPests - minPests)
    
    return scaledValue
  }

  // Check if species has high risk pests (for RAG coloring)
  const hasHighRiskPests = (botanicalName: string): boolean => {
    const result = biosecurityResults.value.get(botanicalName.toLowerCase())
    if (!result) return false
    
    return result.riskLevel === 'major_host'
  }

  // Actions - Update biosecurity results from validation data
  const updateBiosecurityResults = (biosecurityValidationResults: Record<string, any>) => {
    // Clear existing results
    biosecurityResults.value.clear()
    
    if (!biosecurityValidationResults || Object.keys(biosecurityValidationResults).length === 0) {
      console.log('[BiosecurityResultsStore] No biosecurity data provided, clearing results')
      return
    }

    const processedResults: Array<{
      originalName: string
      species: string
      totalPests: number
      majorHostCount: number
      hostCount: number
      weedWildCount: number
      riskLevel: 'major_host' | 'host' | 'weed_wild' | 'none'
    }> = []

    // Process each species' biosecurity data
    Object.entries(biosecurityValidationResults).forEach(([speciesName, data]: [string, any]) => {
      if (!data || !data.pests) return

      const pests = Array.isArray(data.pests) ? data.pests : []
      
      // Count pests by type
      const majorHostCount = pests.filter((pest: any) => pest.type === 'Major host').length
      const hostCount = pests.filter((pest: any) => pest.type === 'Host').length
      const weedWildCount = pests.filter((pest: any) => 
        pest.type === 'Wild/Weed' || pest.type === 'Weed/wild'
      ).length
      
      const totalPests = pests.length

      // Determine risk level for RAG colormap
      let riskLevel: 'major_host' | 'host' | 'weed_wild' | 'none'
      if (majorHostCount > 0) {
        riskLevel = 'major_host' // Highest risk - red
      } else if (hostCount > 0) {
        riskLevel = 'host' // Medium risk - orange  
      } else if (weedWildCount > 0) {
        riskLevel = 'weed_wild' // Low risk - green
      } else {
        riskLevel = 'none' // No pests - neutral
      }

      processedResults.push({
        originalName: speciesName,
        species: speciesName,
        totalPests,
        majorHostCount,
        hostCount,
        weedWildCount,
        riskLevel
      })

      console.log(`[BiosecurityResultsStore] Processed ${speciesName}:`, {
        totalPests,
        majorHost: majorHostCount,
        host: hostCount,
        weedWild: weedWildCount,
        riskLevel
      })
    })

    // Calculate risk scores (0-1 normalized) for sequential/divergent colormaps
    const allPestCounts = processedResults.map(r => r.totalPests)
    const maxPests = allPestCounts.length > 0 ? Math.max(...allPestCounts) : 0
    const minPests = allPestCounts.length > 0 ? Math.min(...allPestCounts) : 0

    // Process each result and store in map
    processedResults.forEach(result => {
      // Calculate normalized risk score (0-1)
      let riskScore = 0
      if (maxPests > minPests) {
        riskScore = (result.totalPests - minPests) / (maxPests - minPests)
      } else if (result.totalPests > 0) {
        riskScore = 0.5 // If all species have same pest count, use middle value
      }

      biosecurityResults.value.set(result.originalName.toLowerCase(), {
        ...result,
        riskScore
      })
    })

    // Update global metrics
    const totalSpecies = processedResults.length
    const totalPests = processedResults.reduce((sum, r) => sum + r.totalPests, 0)
    const averagePestsPerSpecies = totalSpecies > 0 ? totalPests / totalSpecies : 0

    globalBiosecurityMetrics.value = {
      totalSpecies,
      averagePestsPerSpecies,
      maxPests: maxPests || 0,
      minPests: minPests || 0,
      speciesWithMajorHosts: processedResults.filter(r => r.majorHostCount > 0).length,
      speciesWithHosts: processedResults.filter(r => r.hostCount > 0).length,
      speciesWithWeedWild: processedResults.filter(r => r.weedWildCount > 0).length,
      speciesWithNoPests: processedResults.filter(r => r.totalPests === 0).length,
      isValid: totalSpecies > 0
    }

    console.log(`[BiosecurityResultsStore] Updated biosecurity results for ${totalSpecies} species`)
    console.log('[BiosecurityResultsStore] Global metrics:', globalBiosecurityMetrics.value)
    
    // Log pest distribution for debugging color mapping
    const pestDistribution = processedResults
      .map(r => ({ 
        species: r.species, 
        totalPests: r.totalPests,
        scaledPests: maxPests > minPests 
          ? (r.totalPests - minPests) / (maxPests - minPests)
          : 0.5,
        riskLevel: r.riskLevel
      }))
      .sort((a, b) => b.totalPests - a.totalPests)
      .slice(0, 5) // Top 5 highest pest counts
    
    console.log('[BiosecurityResultsStore] Top 5 species with pest scaling:', pestDistribution)
    console.log('[BiosecurityResultsStore] Data range scaling:', {
      originalMin: minPests,
      originalMax: maxPests,
      scalingRange: `${minPests} → 0.0, ${maxPests} → 1.0`,
      fullColormapUtilization: minPests !== maxPests
    })
    
    // Summary of risk levels for RAG coloring
    const riskSummary = processedResults.reduce((acc, result) => {
      acc[result.riskLevel]++
      acc.total++
      return acc
    }, { major_host: 0, host: 0, weed_wild: 0, none: 0, total: 0 })
    
    console.log('[BiosecurityResultsStore] Risk level summary:', riskSummary)
  }

  const clearBiosecurityResults = () => {
    biosecurityResults.value.clear()
    globalBiosecurityMetrics.value = {
      totalSpecies: 0,
      averagePestsPerSpecies: 0,
      maxPests: 0,
      minPests: 0,
      speciesWithMajorHosts: 0,
      speciesWithHosts: 0,
      speciesWithWeedWild: 0,
      speciesWithNoPests: 0,
      isValid: false
    }
    console.log('[BiosecurityResultsStore] Cleared all biosecurity results')
  }

  return {
    // State
    biosecurityResults: readonly(biosecurityResults),
    globalBiosecurityMetrics: readonly(globalBiosecurityMetrics),
    
    // Getters
    getBiosecurityResults,
    getGlobalMetrics,
    
    // Actions
    getBiosecurityDataForName,
    hasBiosecurityResult,
    getRiskLevel,
    getRiskScore,
    getScaledPestCount,
    hasHighRiskPests,
    updateBiosecurityResults,
    clearBiosecurityResults
  }
}) 